/**
 * Plattform-Presets für eCommerce
 *
 * Stand: Januar 2026
 * Quelle: Offizielle Plattform-Dokumentation
 */

import type { PlatformPreset, TaxPreset, CalculatorInput } from '@/types/price-calculator';

export const PLATFORM_PRESETS: Record<string, PlatformPreset> = {
  amazon: {
    id: 'amazon',
    name: 'Amazon (FBM)',
    icon: 'Package',
    description: 'Fulfillment by Merchant - Selbstversand',
    commissionPercent: 15,
    fixedFee: 0,
    paymentFeePercent: 0,
    paymentFixedFee: 0,
    hasFBA: false,
    fbaBasePrice: 0,
    fbaWeightFee: 0,
    monthlySubscription: 39,
    notes: [
      'Provision variiert: 8% (Computer) bis 17% (Kleidung)',
      'Monatliche Gebühr nur bei Professional-Konto',
    ],
  },

  amazon_fba: {
    id: 'amazon_fba',
    name: 'Amazon FBA',
    icon: 'Truck',
    description: 'Fulfillment by Amazon - Amazon versendet',
    commissionPercent: 15,
    fixedFee: 0,
    paymentFeePercent: 0,
    paymentFixedFee: 0,
    hasFBA: true,
    fbaBasePrice: 2.70,
    fbaWeightFee: 0.75,
    monthlySubscription: 39,
    notes: [
      'FBA-Kosten abhängig von Größe/Gewicht',
      'Zusätzlich: Lagerkosten (saisonal variabel)',
      '+0,26€ Zentraleuropa-Zuschlag (nur DE-Lager)',
      '+0,26€ Peak Season Fee (Okt-Jan)',
    ],
  },

  ebay: {
    id: 'ebay',
    name: 'eBay',
    icon: 'Gavel',
    description: 'eBay Marketplace',
    commissionPercent: 11,
    fixedFee: 0.35,
    paymentFeePercent: 2.49,
    paymentFixedFee: 0.35,
    hasFBA: false,
    fbaBasePrice: 0,
    fbaWeightFee: 0,
    monthlySubscription: 0,
    notes: [
      'Provision: 10% (Elektronik) bis 13% (Mode)',
      'eBay Payments Pflicht',
      'Shop-Abos reduzieren Provision',
    ],
  },

  otto: {
    id: 'otto',
    name: 'OTTO Market',
    icon: 'Building',
    description: 'OTTO Marketplace für Partner',
    commissionPercent: 15,
    fixedFee: 0,
    paymentFeePercent: 0,
    paymentFixedFee: 0,
    hasFBA: false,
    fbaBasePrice: 0,
    fbaWeightFee: 0,
    monthlySubscription: 0,
    notes: [
      'Provision kategorie-abhängig (13-17%)',
      'Qualitätsanforderungen beachten',
      'Retourenabwicklung über OTTO',
    ],
  },

  kaufland: {
    id: 'kaufland',
    name: 'Kaufland.de',
    icon: 'ShoppingCart',
    description: 'Kaufland Marketplace',
    commissionPercent: 11.5,
    fixedFee: 0,
    paymentFeePercent: 1.9,
    paymentFixedFee: 0.35,
    hasFBA: false,
    fbaBasePrice: 0,
    fbaWeightFee: 0,
    monthlySubscription: 39.95,
    notes: [
      'Provision: 9% (Elektronik) bis 14% (Mode)',
      'Monatliche Grundgebühr für alle Accounts',
      'Schnelle Onboarding-Zeit',
    ],
  },

  shopify: {
    id: 'shopify',
    name: 'Shopify',
    icon: 'Store',
    description: 'Eigener Shopify-Shop',
    commissionPercent: 0,
    fixedFee: 0,
    paymentFeePercent: 2.4,
    paymentFixedFee: 0.25,
    hasFBA: false,
    fbaBasePrice: 0,
    fbaWeightFee: 0,
    monthlySubscription: 36,
    notes: [
      'Keine Verkaufs-Provision',
      'Nur Payment-Gebühren',
      'Höherer Traffic-Aufwand (Marketing)',
    ],
  },

  custom: {
    id: 'custom',
    name: 'Sonstiger Shop',
    icon: 'Settings',
    description: 'Manuelle Eingabe aller Gebühren',
    commissionPercent: 0,
    fixedFee: 0,
    paymentFeePercent: 2.5,
    paymentFixedFee: 0.35,
    hasFBA: false,
    fbaBasePrice: 0,
    fbaWeightFee: 0,
    monthlySubscription: 0,
    notes: [
      'Alle Werte manuell editierbar',
      'Für WooCommerce, Shopware, etc.',
    ],
  },
};

export const TAX_PRESETS: TaxPreset[] = [
  { id: 'standard', label: '19% (Standard)', rate: 19 },
  { id: 'reduced', label: '7% (Ermäßigt)', rate: 7 },
  { id: 'zero', label: '0% (Steuerfrei)', rate: 0 },
];

export const FBA_GERMANY_SURCHARGE = 0.26;
export const FBA_PEAK_SEASON_SURCHARGE = 0.26;

export const DEFAULT_INPUT: CalculatorInput = {
  purchasePrice: 10.0,
  sellingPrice: 29.99,
  taxRate: 19,
  shippingCost: 4.99,
  shippingToCustomer: 0,
  platformId: 'amazon',
  customCommission: 15,
  customFixedFee: 0,
  useFBA: false,
  productWeight: 0.5,
  fbaStorageCost: 0.5,
  fbaGermanyOnly: false,
  fbaPeakSeason: false,
  packagingCost: 1.0,
  returnRate: 10,
  returnCost: 5.0,
  otherCosts: 0,
};
